import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

// import @material-ui/core
import FormControl from '@material-ui/core/FormControl';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import Backdrop from '@material-ui/core/Backdrop';

// import styles-globalStyles
import globalStyles from './../../../styles/globalStyles';

// import react-router
import { Prompt } from 'react-router-dom';

// import Formik and Dependency
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import * as Yup from 'yup';

// import utils
import { logger } from './../../../utils/utils';

// import fragments
import DropFiles from './../../fragments/DropFiles';
//import fragments { ...Tabs} , AppSidebarRBack, IssueCard, ActivityCard
import { AntTabs, AntTab, TabPanel, a11yProps } from './../../fragments/Tabs';

import { useIssueCommentTypeGetListAll } from './../../../hooks/useData';

// styles
const useStyles = makeStyles((theme) => ({
  ...globalStyles(theme),
  // https://stackoverflow.com/questions/61056265/how-to-override-box-created-by-tabpanel-in-material-ui
  tab: {
    '& .MuiBox-root': {
      padding: theme.spacing(2, 0),
    },
  },

  card: {
    minWidth: 250,
    maxWidth: 930,
    position: 'relative',
  },
  cardContent: {
    minHeight: 200,
    padding: theme.spacing(0, 2),
  },
  section: {
    margin: theme.spacing(1),
  },
  cardAction: {
    width: '100%',
    justifyContent: 'flex-end',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}));

/**
 * FieldComment() (component)
 */
const FieldComment = React.memo(
  ({
    name = 'comment',
    id = 'comment',
    label = 'ความคิดเห็น',
    value = '',
    rowsMax = 6,
    rows = 6,
    disabled = false,
    touched,
    errors,
  }) => {
    return (
      <FormControl fullWidth error={errors && touched ? true : false}>
        <Field
          component={TextField}
          name={name}
          id={id}
          multiline
          rowsMax={rowsMax}
          rows={rows}
          label={label}
          size="small"
          variant="outlined"
          vaule={value}
          disabled={disabled}
        />
      </FormControl>
    );
  }
);
/**
 * ActivityCard's propTypes
 */
FieldComment.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  rowsMax: PropTypes.number,
  rows: PropTypes.number,
};

// YUP: build validation schema (validationSchema)
const validationSchema = Yup.object().shape({
  detail: Yup.string()
    .min(4, 'ความคิดเห็นต้องมากกว่า 4 อักขระ')
    .max(8000, 'ความคิดเห็นต้องไม่เกิน 8,000 อักขระ')
    .required('กรุณากรอกความคิดเห็น'),
});

/**
 * FormComment() (Component)
 * @param {object} props
 * data props
 * @param {object} props.id comment-id
 * @param {object} cardHeader {action, title, subheader}
 * @param {number} props.issueCommentTypeId issue-comment-type-id
 * @param {number} props.receiverType {0, 1}
 * @param {string} props.detail comment detail
 * compoent styles/features props
 * @param {string} props.label
 * @param {number} props.elevation default: 1
 * @param {string} props.variant default: 'outlined'
 * event props
 * @param {bool}   props.receiverTapping default: false,
 * @param {array}  props.existFiles list of object file-thumbnail (structure contains {id, issueId, thumbnail, name, type}) is existed issues that use initail DropFiles
 * @param {func}   props.onDroppedFiles
 * @param {func}   props.onRemovedFile
 * @param {func}   props.onSubmit  Form submit function
 * @param {func}   props.onCancel Form cancel function
 * @param {object} props.formControlStyle formControlStyle
 * @return
 */
const FormComment = ({
  id = undefined,
  cardHeader = {
    action: undefined,
    title: undefined,
    subheader: undefined,
  },
  issueCommentTypeId = 0,
  receiverType = null,
  detail = '',
  label = 'เพิ่มความคิดเห็น',
  elevation = 1,
  variant = 'outlined',
  receiverTapping = false,
  existFiles = [],
  onDroppedFiles,
  onRemovedFile,
  onSubmit,
  onCancel = undefined,
  formControlStyle = undefined,
}) => {
  const classes = useStyles();

  logger('FormComment render');

  // if receiverType = 0 (reply to reprter comment) recieverByTab return index of tab = 1 (tab reply to reprter)
  // if receiverType = 1 (internal comment) recieverByTab return index of tab = 0 (internal)
  const recieverByTab = [1, 0];

  // Issue comment type
  const issueCommentTypes = useIssueCommentTypeGetListAll('TH');

  // AntTab -  State
  const [antTabValue, setAntTabValue] = React.useState(
    receiverTapping && receiverType !== null
      ? recieverByTab.indexOf(receiverType)
      : 0
  );
  const [isLoading, setIsLoading] = useState(false);

  // Ref, dropFilesRef
  const dropFilesRef = useRef();

  // Tab - Handle change tab
  const handleChangeTab = (event, newValue) => {
    setAntTabValue(newValue);
  };

  const handleCancel = (resetForm) => {
    // props.onCancel
    if (onCancel) {
      onCancel();
    }
    // DropFiles - clearFiles
    if (dropFilesRef.current.files.length) {
      dropFilesRef.current.clearFiles();
    }
  };

  // Formik: iniitialValue by data props
  const initialValues = {
    id,
    issueCommentType_id: issueCommentTypeId,
    receiverType: receiverType === null ? 0 : receiverType,
    detail,
  };

  // About select type
  const labelType = 'ประเภทความคิดเห็น';
  const initialTypeValue = 0;
  const [typeValue, setTypeValue] = useState(issueCommentTypeId);
  const typeContextRef = useRef();

  let labeller = label;

  labeller =
    typeValue !== initialTypeValue &&
    antTabValue === 0 &&
    typeContextRef.current
      ? `${label} ประเภท ${typeContextRef.current}`
      : label;

  const formComment = ({
    values,
    setFieldValue,
    resetForm,
    touched,
    errors,
  }) => (
    <div>
      <Form autoComplete="off">
        <Card className={classes.card} elevation={elevation} variant={variant}>
          {(cardHeader.action || cardHeader.title || cardHeader.subtitle) && (
            <CardHeader
              action={cardHeader.action}
              title={cardHeader.title}
              subheader={cardHeader.subheader}
            />
          )}
          <CardContent className={classes.cardContent}>
            {receiverTapping && (
              <React.Fragment>
                <AntTabs
                  value={antTabValue}
                  onChange={handleChangeTab}
                  aria-label="simple tabs example"
                >
                  <AntTab
                    label="ความคิดเห็นภายใน"
                    {...a11yProps(0)}
                    disabled={receiverType != null && receiverType !== 1}
                  />
                  <AntTab
                    label="ตอบผู้แจ้งเหตุ"
                    {...a11yProps(1)}
                    disabled={receiverType != null && receiverType !== 0}
                  />
                </AntTabs>
                <TabPanel value={antTabValue} index={0} className={classes.tab}>
                  <div
                    className={clsx({ [formControlStyle]: formControlStyle })}
                  >
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel
                        htmlFor="issueCommentType_id"
                        style={{ backgroundColor: 'white' }}
                      >
                        {labelType}
                      </InputLabel>
                      <Field
                        component={Select}
                        name="issueCommentType_id"
                        value={typeValue}
                        inputProps={{
                          name: 'issueCommentType_id',
                          id: 'issueCommentType_id',
                        }}
                        onChange={(e, c) => {
                          typeContextRef.current =
                            typeof c.props.children === 'string'
                              ? c.props.children
                              : '';
                          setTypeValue(e.target.value);
                          setFieldValue('issueCommentType_id', e.target.value);
                        }}
                      >
                        {issueCommentTypes.map((item) => (
                          <MenuItem value={item.id} key={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </div>
                </TabPanel>
                <TabPanel
                  value={antTabValue}
                  index={1}
                  className={classes.tab}
                ></TabPanel>
              </React.Fragment>
            )}
            <div
              className={clsx(classes.fieldMargin, {
                [formControlStyle]: formControlStyle,
              })}
            >
              <FieldComment
                name="detail"
                id="detail"
                label={labeller}
                touched={touched.detail ? touched.detail : false}
                errors={errors.detail ? errors.detail : false}
              />
              {onDroppedFiles && (
                <DropFiles
                  ref={dropFilesRef}
                  existFiles={existFiles}
                  onDropped={onDroppedFiles}
                  onRemoved={onRemovedFile}
                />
              )}
            </div>
          </CardContent>
          <Divider
            variant="middle"
            className={clsx({
              [formControlStyle]: formControlStyle,
            })}
          />
          <CardActions
            className={clsx(classes.cardAction, {
              [formControlStyle]: formControlStyle,
            })}
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              บันทึก
            </Button>
            <Button
              variant="contained"
              color="default"
              onClick={() => {
                handleCancel();
                resetForm();
              }}
              disabled={isLoading}
            >
              ยกเลิก
            </Button>
          </CardActions>
          <Backdrop className={classes.innerBackdropStyle} open={isLoading}>
            <CircularProgress className={classes.circularProgressLoadStyle} />
          </Backdrop>
        </Card>
      </Form>
      <Prompt
        when={JSON.stringify(initialValues) !== JSON.stringify(values)}
        message="การเปลี่ยนแปลงข้อมูลจะไม่ถูกบันทึก"
      />
    </div>
  );
  return (
    <React.Fragment>
      <Formik
        validationSchema={validationSchema}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setSubmitting(false);

          // dropFilesRef (ref of DropFiles) has new files and also existFiles is included
          // Note: existFiles in dropFilesRef is data (existFiles) assigned to existFiles (become DropFiles's prop), look DropFiles statement

          let commentFiles = [
            ...dropFilesRef.current.files.map((file) => file.id),
          ].join(',');

          let reqData = {
            id: values.id,
            issueCommentType_id: values.issueCommentType_id,
            receiverType: values.receiverType,
            detail: values.detail,
            commentFiles,
          };
          if (receiverTapping) {
            reqData = {
              ...reqData,
              receiverType: recieverByTab[antTabValue],
            };
          }

          // onSubmit
          (async () => {
            if (onSubmit) {
              !isLoading && setIsLoading(true);
              await onSubmit(reqData);
              setIsLoading(false);
            }
            resetForm();
            dropFilesRef.current && dropFilesRef.current.clearFiles();
          })();

          if (receiverTapping) {
            setTypeValue(initialTypeValue);
          }
        }}
      >
        {(props) => formComment(props)}
      </Formik>
    </React.Fragment>
  );
};

export { FieldComment, FormComment };
